import React, { type ReactNode, type PointerEvent, useMemo } from 'react';
import { styled } from '@compiled/react';
import Link from '@jira/platform__link-with-analytics/src/LinkWithAnalytics.tsx';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';

type Props = {
	caption: ReactNode;
	href: string;
	onClick: (event: PointerEvent, analyticsEvent: AnalyticsEvent) => void;
};

export default function LinkCell({ caption, href, onClick, ...props }: Props): React.JSX.Element {
	const content = useMemo(() => {
		const innerContent = (
			<Box paddingInline="space.050">
				{href ? (
					<Link href={href} onClick={onClick}>
						{caption}
					</Link>
				) : (
					caption
				)}
			</Box>
		);

		if (isVisualRefreshEnabled()) {
			return (
				<Box xcss={containerStyles}>
					<Box xcss={wrapperStyles}>
						<Tooltip
							content={caption}
							isScreenReaderAnnouncementDisabled
							delay={1000}
							position="bottom-start"
						>
							{innerContent}
						</Tooltip>
					</Box>
				</Box>
			);
		}

		return (
			<Container {...props}>
				<Tooltip
					content={caption}
					isScreenReaderAnnouncementDisabled
					delay={1000}
					position="bottom-start"
				>
					{innerContent}
				</Tooltip>
			</Container>
		);
	}, [caption, href, onClick, props]);

	return content;
}

const containerStyles = xcss({
	width: '100%',
	paddingBlock: 'space.100',
	flex: '1 1 auto',
	flexDirection: 'row',
	display: 'flex',
	height: '40px',
});

const wrapperStyles = xcss({
	paddingBlock: 'space.025',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Container = styled.div({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'row',
	width: '100%',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
});
